import { aggregate } from '@evidentid/ido-lib/aggregation';
import { curate } from '@evidentid/ido-lib/curation';
import getIdoRequestStatus, { IdoRequestStatus, RelyingPartyRequestStatusType } from '@evidentid/ido-lib/getIdoRequestStatus';
import { getIdentity, getIdoProfile, getVerificationRequestDetails } from '@evidentid/ido-lib/idoWebClient';
import { RelyingPartyRequestFeatureFlags } from '@evidentid/ido-lib/interfaces/AttributeRelyingPartyRequest';
import AttributeSharingAgreement from '@evidentid/ido-lib/interfaces/AttributeSharingAgreement';
import Identity, { ConsentHtml } from '@evidentid/ido-lib/interfaces/Identity';
import Question from '@evidentid/ido-lib/interfaces/Question';

import { BIOMETRIC_CONSENT_BODY_HTML } from '@/config/biometricConsent';
import { defaultPrivacyPolicy } from '@/config/issuer';
import getFieldType from '@/fields/getFieldType';
import Balance from '@/interfaces/Balance';
import { RegularSessionData } from '@/interfaces/SessionData';
import UserData from '@/interfaces/UserData';
import { GlsData } from '@/services/getGlsSessionInterview';
import { ValuesMap } from '@/store/interfaces/IdoState';

export interface SessionInterviewDetails {
    user: UserData;
    issuer: Identity;
    gls?: GlsData;
    questions: Question[];
    balance: Balance | null;
    summary: string | null;
    status: IdoRequestStatus;
    featureFlags: RelyingPartyRequestFeatureFlags;
    values?: ValuesMap;
    attributeSharingAgreements?: AttributeSharingAgreement[] | null;
}

export const defaultConsentHtmls: Record<string, ConsentHtml> = {
    'consent.biometric.authorization': {
        data: BIOMETRIC_CONSENT_BODY_HTML,
        mime_type: 'text/html',
        metadata: {},
    },
};

export const defaultFeatureFlags: RelyingPartyRequestFeatureFlags = {
    rpr_constant_polling_enabled: true,
};

const IMMEDIATE_FINISH_STATUSES = [
    RelyingPartyRequestStatusType.complete,
    RelyingPartyRequestStatusType.processing,
];

export default async function getSessionInterview(
    sessionData: RegularSessionData,
    existingUser?: UserData | null,
    existingIssuer?: Identity | null,
    blockImmediateFinish?: boolean,
): Promise<SessionInterviewDetails> {
    // Load or reuse required data
    const user = existingUser ? existingUser : await getIdoProfile();
    const rpRequest = await getVerificationRequestDetails(sessionData.rprId);
    const originalIssuer = existingIssuer || await getIdentity(rpRequest.relyingparty_id);
    const issuer = { ...originalIssuer };
    const status = getIdoRequestStatus(rpRequest);

    // Patch issuer consents, to have the hardcoded default values too
    issuer.consentHtml = { ...defaultConsentHtmls, ...issuer.consentHtml };
    issuer.privacyPolicy = { ...defaultPrivacyPolicy, ...issuer.privacyPolicy };

    // When user has been delegated to external flow,
    // we would like to simulate the final transition in current session too.
    if (blockImmediateFinish && IMMEDIATE_FINISH_STATUSES.includes(status.type)) {
        status.type = RelyingPartyRequestStatusType.new;
    }

    // Build information about current RP request
    const aggregated = aggregate(rpRequest, issuer) as any;
    // Detect US-only Incode requests from pre-curation attributes — after curation the
    // US doc attrs are removed from the question list (merged into the liveness question),
    // so this check must happen before curate() is called.
    const isUsOnlyIncode = Object.keys(aggregated.input || {}).some(
        (at: string) => at.startsWith('identity_assurance.document_verification.americas.us'),
    );
    const requirements = curate(aggregated);
    const attributeSharingAgreements = rpRequest.attribute_sharing_agreements;
    const questions = Object.values(requirements.input).filter(Boolean).map((question) => {
        const type = getFieldType(question);
        if (isUsOnlyIncode && type === 'liveness-incode') {
            return { ...question, type, metadata: { ...question.metadata, isUsOnly: true } };
        }
        return { ...question, type };
    }) as Question[];

    return {
        user,
        balance: requirements.balance,
        issuer,
        questions,
        status,
        featureFlags: {
            ...defaultFeatureFlags,
            ...rpRequest.feature_flags,
        },
        summary: rpRequest.summary || null,
        attributeSharingAgreements,
    };
}
